import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ArrowRight, Menu } from "lucide-react";
import Logo from "@/brand/Logo";
import Icon from "@/components/Icon";
import { SOLUTIONS, PLATFORMS } from "@/data/site";
import MobileMenu from "@/components/layout/MobileMenu";
import { useHeaderTheme } from "@/context/HeaderThemeContext";

function MegaPanel({ open, children }) {
  return (
    <div
      className={`absolute left-1/2 top-full z-40 -translate-x-1/2 pt-3 transition-all duration-200 ${
        open ? "visible opacity-100 translate-y-0" : "invisible opacity-0 -translate-y-1"
      }`}
    >
      <div className="rounded-2xl border border-border bg-white p-5 shadow-panel">{children}</div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [scrollable, setScrollable] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { theme } = useHeaderTheme();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrollable(max > 400);
      setProgress(max > 0 ? Math.min(100, (y / max) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Adaptive coloring: light (white) menu only when transparent AND over a dark hero image.
  const light = !scrolled && theme === "light";
  const triggerClass = `flex items-center gap-1 text-[15px] font-medium transition-colors ${
    light ? "text-white/90 hover:text-white" : "text-cbink hover:text-primary"
  }`;
  const navLinkClass = ({ isActive }) =>
    `text-[15px] font-medium transition-colors ${
      isActive ? (light ? "text-white" : "text-primary") : light ? "text-white/90 hover:text-white" : "text-cbink hover:text-primary"
    }`;

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 w-full transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-b border-white/40 bg-white/70 shadow-soft backdrop-blur-xl supports-[backdrop-filter]:bg-white/60"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      {/* legibility scrim over hero images when transparent (dark scrim over dark images) */}
      {!scrolled && (
        <div
          className={`pointer-events-none absolute inset-0 -z-10 ${
            light
              ? "bg-gradient-to-b from-black/45 via-black/15 to-transparent"
              : "bg-gradient-to-b from-white/55 via-white/20 to-transparent"
          }`}
          aria-hidden="true"
        />
      )}

      <div className="container-cb flex h-[96px] items-center justify-between gap-6">
        <Link
          to="/"
          aria-label="Cloud Beaker home"
          data-testid="nav-logo"
          className={`shrink-0 transition-[filter] duration-300 ${light ? "drop-shadow-[0_1px_6px_rgba(0,0,0,0.45)]" : ""}`}
        >
          <Logo size={70} />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex xl:gap-7" aria-label="Primary">
          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("solutions")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button className={triggerClass} data-testid="nav-solutions" aria-expanded={openMenu === "solutions"}>
              Solutions <ChevronDown size={15} className={`transition-transform ${openMenu === "solutions" ? "rotate-180" : ""}`} />
            </button>
            <MegaPanel open={openMenu === "solutions"}>
              <div className="grid w-[720px] grid-cols-2 gap-1.5">
                {SOLUTIONS.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/solutions/${s.slug}`}
                    className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-cbmist"
                  >
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cbblue-soft text-primary">
                      <Icon name={s.icon} size={18} />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-cbink group-hover:text-primary">{s.title}</span>
                      <span className="block text-xs text-cbslate line-clamp-1">{s.tagline}</span>
                    </span>
                  </Link>
                ))}
                <Link to="/solutions" className="col-span-2 mt-1 flex items-center justify-between rounded-xl bg-cbmist px-4 py-3 text-sm font-semibold text-primary">
                  View all solutions <ArrowRight size={16} />
                </Link>
              </div>
            </MegaPanel>
          </div>

          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("platforms")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button className={triggerClass} data-testid="nav-platforms" aria-expanded={openMenu === "platforms"}>
              Platforms <ChevronDown size={15} className={`transition-transform ${openMenu === "platforms" ? "rotate-180" : ""}`} />
            </button>
            <MegaPanel open={openMenu === "platforms"}>
              <div className="grid w-[560px] grid-cols-2 gap-1.5">
                {PLATFORMS.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/platforms/${p.slug}`}
                    className="group flex flex-col rounded-xl p-3 transition-colors hover:bg-cbmist"
                  >
                    <span className="text-sm font-semibold text-cbink group-hover:text-primary">{p.title}</span>
                    <span className="text-xs text-cbslate">{p.relationship}</span>
                  </Link>
                ))}
              </div>
            </MegaPanel>
          </div>

          <NavLink to="/why" className={navLinkClass} data-testid="nav-why">Why Cloud Beaker</NavLink>
          <NavLink to="/industries" className={navLinkClass} data-testid="nav-industries">Industries</NavLink>
          <NavLink to="/resources" className={navLinkClass} data-testid="nav-resources">Resources</NavLink>
          <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden items-center gap-4 lg:flex shrink-0">
          <Link to="/portal" className="btn-primary" data-testid="nav-cta-portal">
            Client Portal <ArrowRight size={16} />
          </Link>
        </div>

        <button
          className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors lg:hidden ${
            light ? "border-white/50 text-white" : "border-border text-cbink"
          }`}
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          data-testid="nav-mobile-toggle"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Reading progress bar (long pages only) */}
      {scrollable && (
        <div className="absolute inset-x-0 bottom-0 h-[3px] bg-transparent" aria-hidden="true" data-testid="reading-progress">
          <div
            className="h-full bg-primary transition-[width] duration-150 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}
